import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppSelector, useAppDispatch } from '../../store';
import { removeToast } from '../../store/slices/uiSlice';

const Toast: React.FC = () => {
  const dispatch = useAppDispatch();
  const { toasts } = useAppSelector(state => state.ui);
  const [progress, setProgress] = useState<{ [key: string]: number }>({});

  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];
    const intervals: NodeJS.Timeout[] = [];

    toasts.forEach(toast => {
      if (toast.duration && toast.duration > 0) {
        setProgress(prev => ({ ...prev, [toast.id]: 100 }));
        const startTime = Date.now();
        const duration = toast.duration;
        const interval = setInterval(() => {
          const elapsed = Date.now() - startTime;
          const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
          setProgress(prev => ({ ...prev, [toast.id]: remaining }));
        }, 50);
        intervals.push(interval);
        const timer = setTimeout(() => {
          dispatch(removeToast(toast.id));
          setProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[toast.id];
            return newProgress;
          });
        }, toast.duration);

        timers.push(timer);
      }
    });

    return () => {
      timers.forEach(timer => clearTimeout(timer));
      intervals.forEach(interval => clearInterval(interval));
    };
  }, [toasts, dispatch]);

  const getToastIcon = (type: string) => {
    switch (type) {
      case 'success':
        return (
          <div className="relative flex items-center justify-center">
            <div className="absolute inset-0 bg-green-500 rounded-full opacity-20 animate-ping" />
            <svg className="w-5 h-5 text-white relative z-10" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        );
      case 'error':
        return (
          <div className="relative flex items-center justify-center">
            <div className="absolute inset-0 bg-red-500 rounded-full opacity-20 animate-ping" />
            <svg className="w-5 h-5 text-white relative z-10" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </div>
        );
      case 'warning':
        return (
          <div className="relative flex items-center justify-center">
            <div className="absolute inset-0 bg-amber-500 rounded-full opacity-20 animate-ping" />
            <svg className="w-5 h-5 text-white relative z-10" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
        );
      case 'info':
        return (
          <div className="relative flex items-center justify-center">
            <div className="absolute inset-0 bg-blue-500 rounded-full opacity-20 animate-ping" />
            <svg className="w-5 h-5 text-white relative z-10" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  const getToastConfig = (type: string) => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-gradient-to-br from-green-50 to-emerald-50/80',
          border: 'border-green-200/60',
          shadow: 'shadow-lg shadow-green-500/10',
          iconBg: 'bg-gradient-to-br from-green-500 to-emerald-600',
          textTitle: 'text-green-900',
          textMessage: 'text-green-700',
          progressBg: 'bg-green-500',
          closeHover: 'hover:bg-green-100',
          ring: 'focus:ring-green-300',
        };
      case 'error':
        return {
          bg: 'bg-gradient-to-br from-red-50 to-rose-50/80',
          border: 'border-red-200/60',
          shadow: 'shadow-lg shadow-red-500/10',
          iconBg: 'bg-gradient-to-br from-red-500 to-rose-600',
          textTitle: 'text-red-900',
          textMessage: 'text-red-700',
          progressBg: 'bg-red-500',
          closeHover: 'hover:bg-red-100',
          ring: 'focus:ring-red-300',
        };
      case 'warning':
        return {
          bg: 'bg-gradient-to-br from-amber-50 to-yellow-50/80',
          border: 'border-amber-200/60',
          shadow: 'shadow-lg shadow-amber-500/10',
          iconBg: 'bg-gradient-to-br from-amber-500 to-orange-600',
          textTitle: 'text-amber-900',
          textMessage: 'text-amber-700',
          progressBg: 'bg-amber-500',
          closeHover: 'hover:bg-amber-100',
          ring: 'focus:ring-amber-300',
        };
      case 'info':
        return {
          bg: 'bg-gradient-to-br from-blue-50 to-cyan-50/80',
          border: 'border-blue-200/60',
          shadow: 'shadow-lg shadow-blue-500/10',
          iconBg: 'bg-gradient-to-br from-blue-500 to-cyan-600',
          textTitle: 'text-blue-900',
          textMessage: 'text-blue-700',
          progressBg: 'bg-blue-500',
          closeHover: 'hover:bg-blue-100',
          ring: 'focus:ring-blue-300',
        };
      default:
        return {
          bg: 'bg-gradient-to-br from-gray-50 to-slate-50/80',
          border: 'border-gray-200/60',
          shadow: 'shadow-lg shadow-gray-500/10',
          iconBg: 'bg-gradient-to-br from-gray-500 to-slate-600',
          textTitle: 'text-gray-900',
          textMessage: 'text-gray-700',
          progressBg: 'bg-gray-500',
          closeHover: 'hover:bg-gray-100',
          ring: 'focus:ring-gray-300',
        };
    }
  };

  return (
    <div className="fixed top-4 right-4 z-[9999] space-y-3 max-w-md pointer-events-none">
      <AnimatePresence mode="popLayout">
        {toasts.map((toast) => {
          const config = getToastConfig(toast.type);

          return (
            <motion.div
              key={toast.id}
              layout
              initial={{ opacity: 0, x: 400, scale: 0.85, rotateZ: 10 }}
              animate={{
                opacity: 1,
                x: 0,
                scale: 1,
                rotateZ: 0,
                transition: {
                  type: "spring",
                  stiffness: 300,
                  damping: 30,
                }
              }}
              exit={{
                opacity: 0,
                x: 400,
                scale: 0.85,
                rotateZ: -10,
                transition: {
                  duration: 0.2,
                  ease: "easeIn"
                }
              }}
              whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
              className={`
                w-full min-w-[320px] max-w-sm pointer-events-auto
                rounded-2xl border backdrop-blur-xl
                ${config.bg} ${config.border} ${config.shadow}
                overflow-hidden
              `}
            >
              {/* Content */}
              <div className="px-4 py-4">
                <div className="flex items-start gap-3">
                  {/* Icon Container */}
                  <div className={`
                    flex-shrink-0 w-10 h-10 rounded-xl ${config.iconBg}
                    flex items-center justify-center
                    shadow-md ring-2 ring-white/20
                  `}>
                    {getToastIcon(toast.type)}
                  </div>

                  {/* Text Content */}
                  <div className="flex-1 min-w-0 pt-0.5">
                    <p className={`text-sm font-semibold leading-snug ${config.textTitle}`}>
                      {toast.title}
                    </p>
                    {toast.message && (
                      <p className={`mt-1.5 text-sm leading-relaxed ${config.textMessage}`}>
                        {toast.message}
                      </p>
                    )}
                  </div>

                  {/* Close Button */}
                  <div className="flex-shrink-0">
                    <button
                      className={`
                        p-1.5 rounded-lg transition-all duration-200
                        ${config.closeHover} ${config.ring}
                        focus:outline-none focus:ring-2
                        ${config.textTitle} hover:scale-110 active:scale-95
                      `}
                      onClick={() => dispatch(removeToast(toast.id))}
                      aria-label="Close notification"
                    >
                      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              {toast.duration && toast.duration > 0 && (
                <div className="h-1 bg-black/5 relative overflow-hidden">
                  <motion.div
                    className={`absolute inset-y-0 left-0 ${config.progressBg}`}
                    initial={{ width: '100%' }}
                    animate={{ width: `${progress[toast.id] || 0}%` }}
                    transition={{ ease: 'linear' }}
                  />
                </div>
              )}
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

export default Toast;
